import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { mascaraTelefoneBR } from '@fiodecobre/shared';
import { appApi, setTokenApp, setTelefoneLembrado, ErroApp, type ClienteApp } from './api';
import { CampoSenha, estiloCampo } from './campos';

// "Esqueci minha senha" (02/09/2026).
//
// ⚠️ **Isto nao libera nada sozinho.** Quem abre a porta e' a LOJA, pelo botao
// Resetar senha em Configuracoes → AppCel, e a liberacao vale 2 horas. Um
// "esqueci minha senha" que se auto-libera (por SMS, por e-mail) mudaria a
// natureza deste app: aqui quem diz que a pessoa e' ela mesma e' a loja, que a
// conhece — a mesma regra do convite de 1o acesso, que existe justamente porque
// qualquer um sabe o telefone de uma empresa ([`EntradaApp`]).
//
// ⚠️ **A pergunta vem ANTES dos campos de senha.** Deixar a pessoa escolher e
// repetir a senha pra so entao dizer "a loja nao liberou" e' a recusa que aparece
// depois do trabalho feito ([DA-560]) — e aqui ela apontaria pra uma porta que a
// tela nao tem como abrir.
//
// ⚠️ **Sem senha antiga, aqui.** Este e' o unico caminho do app que grava senha
// sem conferir a anterior, e o que o segura e' a janela de 2h aberta pela loja.
// Quem lembra da senha troca em ☰ → Config → Trocar senha, que exige a atual.

const COBRE = '#b45309';

type Etapa = 'numero' | 'senha';

export function EsqueciSenhaApp({ telefoneInicial, avisoInicial = null, aoEntrar, aoVoltar }: {
  telefoneInicial: string;
  /** Por que a tela abriu sozinha — so vem quando o login mandou pra ca ([`EntradaApp`]).
   *  Sai da tela no primeiro passo dado, porque ai ja deixou de ser novidade. */
  avisoInicial?: string | null;
  aoEntrar: (cliente: ClienteApp) => void;
  aoVoltar: () => void;
}): React.ReactElement {
  const [etapa, setEtapa] = useState<Etapa>('numero');
  const [telefone, setTelefone] = useState(telefoneInicial);
  const [nome, setNome] = useState<string | null>(null);
  const [senha, setSenha] = useState('');
  const [repetida, setRepetida] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const telefoneOk = telefone.replace(/\D/g, '').length >= 10;

  async function conferir(): Promise<void> {
    if (!telefoneOk || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await appApi.redefinicaoLiberada(telefone);
      if (!r.liberado) {
        // ⚠️ Texto unico pros tres casos (numero sem conta, conta sem reset, janela
        // vencida) — separar entregaria, a quem esta chutando numero, quais tem
        // cadastro na loja. E ele diz O QUE FAZER, que e' o que falta a quem chegou
        // aqui: sem isso a tela vira porta trancada sem campainha.
        setErro('A loja ainda não liberou uma senha nova para este número. Fale com a gente no WhatsApp e peça a liberação — ela vale por 2 horas.');
        return;
      }
      setNome(r.nome);
      setEtapa('senha');
    } catch (e) {
      setErro((e as ErroApp).message || 'Não consegui conferir agora.');
    } finally {
      setOcupado(false);
    }
  }

  async function criar(): Promise<void> {
    if (impedimento || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      const sessao = await appApi.redefinir(telefone, senha);
      setTokenApp(sessao.token);
      setTelefoneLembrado(mascaraTelefoneBR(sessao.cliente.telefone));
      aoEntrar(sessao.cliente);
    } catch (e) {
      setErro((e as ErroApp).message || 'Não consegui criar a senha agora.');
      // ⚠️ Volta pro comeco quando a janela morreu no meio (403): insistir no botao
      // com os mesmos campos nao tem como dar certo, e a tela precisa dizer isso.
      if ((e as ErroApp).status === 403) setEtapa('numero');
    } finally {
      setOcupado(false);
    }
  }

  // Botao travado DIZ por que (`CLAUDE.md` raiz, § UI).
  const impedimento =
    senha.length < 6 ? 'A senha precisa de pelo menos 6 caracteres.'
    : repetida !== senha ? 'As duas senhas estão diferentes.'
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button
        type="button"
        onClick={aoVoltar}
        style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4, color: COBRE, fontSize: 14.5, fontWeight: 700 }}
      >
        <ChevronLeft size={18} /> Voltar
      </button>

      <h2 style={{ fontSize: 19, fontWeight: 800 }}>
        {etapa === 'numero' ? 'Esqueci minha senha' : 'Crie a sua nova senha'}
      </h2>

      {etapa === 'numero' ? (
        <>
          {avisoInicial && (
            // Cor da marca, nao vermelho: isto NAO e' erro — e' a tela dizendo que
            // ja achou o caminho. Vermelho aqui leria como "deu ruim de novo".
            <div style={{ backgroundColor: '#fff8ef', border: `1px solid ${COBRE}`, color: '#5b4636', padding: '10px 12px', borderRadius: 8, fontSize: 14, lineHeight: 1.5 }}>
              {avisoInicial}
            </div>
          )}
          <p style={{ fontSize: 14, color: '#5b4636', lineHeight: 1.5 }}>
            Peça à loja para liberar uma senha nova para o seu celular. Feito isso,
            digite o número aqui e escolha a senha que você quiser.
          </p>
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Celular com DDD</span>
            <input
              className="no-upper"
              style={{ ...estiloCampo, marginTop: 6 }}
              value={telefone}
              onChange={(e) => { setTelefone(mascaraTelefoneBR(e.target.value)); setErro(null); }}
              inputMode="numeric"
              autoComplete="tel"
              placeholder="(64) 90000-0000"
            />
          </label>
        </>
      ) : (
        <>
          <p style={{ fontSize: 14, color: '#5b4636', lineHeight: 1.5 }}>
            {/* Ver o proprio nome e' o que diz "e' a sua conta" — e ele so chega
                aqui porque a loja ja liberou este numero. */}
            {nome ? <>Liberado para <b>{nome}</b>. </> : null}
            Escolha uma senha e você já entra.
          </p>
          <CampoSenha
            rotulo="Nova senha"
            valor={senha}
            aoMudar={(v) => { setSenha(v); setErro(null); }}
            autoComplete="new-password"
            placeholder="Pelo menos 6 caracteres"
          />
          {/* Cada campo com o SEU olho: quem confere se a repeticao bate precisa
              abrir uma e manter a outra escondida. */}
          <CampoSenha
            rotulo="Repita a senha"
            valor={repetida}
            aoMudar={(v) => { setRepetida(v); setErro(null); }}
            autoComplete="new-password"
          />
        </>
      )}

      {erro && (
        <div style={{ backgroundColor: '#fef2f2', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, fontSize: 14, lineHeight: 1.5 }}>
          {erro}
        </div>
      )}

      <button
        type="button"
        onClick={() => void (etapa === 'numero' ? conferir() : criar())}
        disabled={ocupado || (etapa === 'numero' ? !telefoneOk : !!impedimento)}
        style={{
          height: 52, borderRadius: 10, border: 'none',
          backgroundColor: COBRE, color: '#fff', fontSize: 16, fontWeight: 700,
          opacity: ocupado || (etapa === 'numero' ? !telefoneOk : !!impedimento) ? 0.5 : 1,
        }}
      >
        {ocupado ? 'Aguarde...' : etapa === 'numero' ? 'Continuar' : 'Criar senha e entrar'}
      </button>

      {etapa === 'senha' && impedimento && (
        <div style={{ fontSize: 13, color: '#92400e', backgroundColor: '#fffbeb', padding: '10px 12px', borderRadius: 8 }}>
          {impedimento}
        </div>
      )}
    </div>
  );
}
